import { useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

/**
 * Hook to ensure user profile records exist after authentication
 * This acts as a safety net to create any missing profile records
 */
export function useProfileSetup() {
  const { user } = useAuth();
  
  useEffect(() => {
    // Only run if we have an authenticated user
    if (!user?.id) return;
    
    const setupUserProfile = async () => {
      try {
        console.log(`Checking profile setup for user: ${user.id}`);
        
        // Check if profile exists
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id')
          .eq('id', user.id)
          .single();
          
        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Error checking profile:', profileError);
        }
        
        // If no profile found, create one using our SQL function
        if (!profileData) {
          console.log('Profile not found, creating...');
          
          // Get display name from user metadata or email
          const displayName = user.email ? user.email.split('@')[0] : 'User';
          
          const { error } = await supabase.rpc('create_user_profile', {
            user_id_param: user.id,
            email_param: user.email || '',
            full_name_param: displayName
          });
          
          if (error) {
            console.error('Error creating profile:', error);
            toast.error('Profile setup issue', {
              description: 'There was an issue setting up your profile. Some features may be limited.'
            });
          } else {
            console.log('Profile created successfully');
            // Force reload to refresh the app with new profile data
            window.location.reload();
          }
        } else {
          console.log('Profile exists, checking for account...');
          
          // Check if user has at least one account
          const { data: accountsData, error: accountsError } = await supabase
            .from('accounts')
            .select('id')
            .eq('user_id', user.id)
            .limit(1);
            
          if (accountsError) {
            console.error('Error checking accounts:', accountsError);
          }
          
          // If no accounts found, create a default cash account
          if (!accountsData || accountsData.length === 0) {
            console.log('No accounts found, creating default account...');
            
            const { error: createError } = await supabase
              .from('accounts')
              .insert({
                user_id: user.id,
                name: 'Cash',
                type: 'cash',
                currency: 'USD',
                balance: 0,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              });
              
            if (createError) {
              console.error('Error creating default account:', createError);
            } else {
              console.log('Default account created successfully');
              // Reload to refresh accounts data
              window.location.reload();
            }
          }
        }
      } catch (error) {
        console.error('Unexpected error in profile setup:', error);
      }
    };
    
    setupUserProfile();
  }, [user?.id]);
  
  return null; // This hook doesn't return anything
} 